import { useState, useEffect, useMemo, useCallback } from 'react';
import DateObject from 'react-date-object';
import persian from 'react-date-object/calendars/persian';
import gregorian from 'react-date-object/calendars/gregorian';
import persian_fa from 'react-date-object/locales/persian_fa';
import gregorian_en from 'react-date-object/locales/gregorian_en';

// Format number with thousand separators (every 3 digits)
export const formatNumber = (num, decimalPlaces = 2) => {
  if (num === null || num === undefined || num === '') return '';
  const number = typeof num === 'string' ? parseFloat(num.replace(/,/g, '')) : num;
  if (isNaN(number)) return '';
  
  // Convert to string and split by decimal point
  const parts = number.toString().split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1];
  
  // Add thousand separators to integer part and limit decimal places
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const formattedDecimal = decimalPart ? decimalPart.slice(0, decimalPlaces) : '';
  
  // Return formatted number with decimal part if exists
  return decimalPart ? `${formattedInteger}.${formattedDecimal}` : formattedInteger;
};

// Parse formatted number string to number
export const parseFormattedNumber = (str) => {
  if (!str || str === '') return '';
  return str.replace(/,/g, '');
};

// Convert Persian date to Gregorian date (YYYY-MM-DD format for database)
const DATE_CONFIGS = {
  fa: {
    calendar: persian,
    locale: persian_fa,
    format: 'YYYY/MM/DD',
  },
  en: {
    calendar: gregorian,
    locale: gregorian_en,
    format: 'YYYY-MM-DD',
  },
};

export const getDateConfig = (locale = 'fa') => DATE_CONFIGS[locale] || DATE_CONFIGS.fa;

export const getTodayByLocale = (locale = 'fa') => {
  const config = getDateConfig(locale);
  const today = new DateObject().convert(config.calendar);
  return today;
};

export const getMonthStartByLocale = (locale = 'fa') => {
  const start = new DateObject(getTodayByLocale(locale));
  start.day = 1;
  return start;
};

export const convertDateForApi = (dateObject, locale = 'fa') => {
  if (!dateObject) return null;
  const base = new DateObject(dateObject).convert(gregorian);
  const year = base.year;
  const month = String(base.month).padStart(2, '0');
  const day = String(base.day).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatDateForDisplay = (dateString, locale = 'fa') => {
  if (!dateString) return '';
  try {
    const config = getDateConfig(locale);
    const date = new DateObject(dateString).convert(config.calendar);
    return date.format(config.format);
  } catch (e) {
    return '';
  }
};

export const switchDateLocale = (value, fromLocale = 'fa', toLocale = 'fa') => {
  if (!value || fromLocale === toLocale) return value;
  const current = value instanceof DateObject ? new DateObject(value) : new DateObject(value);
  const gregorianDate = current.convert(gregorian);
  const targetConfig = getDateConfig(toLocale);
  return new DateObject(gregorianDate).convert(targetConfig.calendar);
};

// Legacy helpers retained for compatibility
export const persianToGregorian = (dateObject) => convertDateForApi(dateObject, 'fa');

export const gregorianToPersian = (dateString) => formatDateForDisplay(dateString, 'fa');

// Shared pagination hook for table data
export const usePagination = (items = [], config = {}) => {
  const safeItems = Array.isArray(items) ? items : [];
  const { initialPageSize = 10, pageSizeOptions = [10, 25, 50] } = config;

  const normalizedOptions = useMemo(() => {
    const options = Array.isArray(pageSizeOptions) && pageSizeOptions.length > 0
      ? pageSizeOptions
      : [initialPageSize];
    const unique = Array.from(new Set([...options, initialPageSize])).filter((value) => value > 0);
    return unique.sort((a, b) => a - b);
  }, [pageSizeOptions, initialPageSize]);

  const [pageSize, setPageSize] = useState(() => {
    if (normalizedOptions.includes(initialPageSize)) {
      return initialPageSize;
    }
    return normalizedOptions[0] || 10;
  });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!normalizedOptions.includes(pageSize)) {
      setPageSize(normalizedOptions[0] || 10);
    }
  }, [normalizedOptions, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  const totalItems = safeItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
    if (currentPage < 1) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return safeItems.slice(startIndex, startIndex + pageSize);
  }, [safeItems, currentPage, pageSize]);

  const goToPage = useCallback((page) => {
    setCurrentPage((prev) => {
      if (typeof page !== 'number' || Number.isNaN(page)) {
        return prev;
      }
      if (page < 1) {
        return 1;
      }
      if (page > totalPages) {
        return totalPages;
      }
      return page;
    });
  }, [totalPages]);

  const nextPage = useCallback(() => {
    goToPage(currentPage + 1);
  }, [goToPage, currentPage]);

  const prevPage = useCallback(() => {
    goToPage(currentPage - 1);
  }, [goToPage, currentPage]);

  const rangeStart = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = totalItems === 0 ? 0 : Math.min(currentPage * pageSize, totalItems);

  return {
    currentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalItems,
    paginatedItems,
    nextPage,
    prevPage,
    goToPage,
    canGoNext: currentPage < totalPages,
    canGoPrev: currentPage > 1,
    pageSizeOptions: normalizedOptions,
    rangeStart,
    rangeEnd,
  };
};

export const calculateUsdEquivalent = (withdrawal) => {
  if (!withdrawal) {
    return null;
  }

  const { amount, exchange_rate: exchangeRate, currency } = withdrawal;

  if (amount === null || amount === undefined) {
    return null;
  }

  const amountValue = parseFloat(amount);

  if (Number.isNaN(amountValue)) {
    return null;
  }

  if (currency === 'USD') {
    return amountValue;
  }

  if (currency === 'IRR') {
    if (exchangeRate === null || exchangeRate === undefined) {
      return null;
    }

    const rateValue = parseFloat(exchangeRate);

    if (Number.isNaN(rateValue) || rateValue <= 0) {
      return null;
    }

    const usdValue = amountValue / rateValue;

    if (!Number.isFinite(usdValue)) {
      return null;
    }

    return parseFloat(usdValue.toFixed(2));
  }

  return null;
};

